import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

// Step 3: Problem Statement & SMART Goals
export const Step3 = ({ formData, updateField, updateNestedField }) => {
  const addOutcome = () => {
    const outcomes = [...formData.expectedOutcomes, ''];
    updateField('expectedOutcomes', outcomes);
  };

  const removeOutcome = (index) => {
    const outcomes = formData.expectedOutcomes.filter((_, i) => i !== index);
    updateField('expectedOutcomes', outcomes);
  };

  const updateOutcome = (index, value) => {
    const outcomes = [...formData.expectedOutcomes];
    outcomes[index] = value;
    updateField('expectedOutcomes', outcomes);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Goals & Impact</h2>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Problem Statement <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.problemStatement}
          onChange={(e) => updateField('problemStatement', e.target.value)}
          placeholder="What problem does this project address?"
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Beneficiary Category <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.targetBeneficiaries.category}
            onChange={(e) => updateNestedField('targetBeneficiaries', 'category', e.target.value)}
            placeholder="e.g., Children, Women, Rural Population"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Beneficiaries <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={formData.targetBeneficiaries.count}
            onChange={(e) => updateNestedField('targetBeneficiaries', 'count', parseInt(e.target.value))}
            placeholder="0"
            min="0"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Demographics (Optional)
        </label>
        <textarea
          value={formData.targetBeneficiaries.demographics}
          onChange={(e) => updateNestedField('targetBeneficiaries', 'demographics', e.target.value)}
          placeholder="Additional details about target beneficiaries..."
          rows={2}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
      </div>

      <div className="border-t pt-6 mt-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">SMART Goals</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specific <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.smartGoals.specific}
              onChange={(e) => updateNestedField('smartGoals', 'specific', e.target.value)}
              placeholder="What exactly will be accomplished?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Measurable <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.smartGoals.measurable}
              onChange={(e) => updateNestedField('smartGoals', 'measurable', e.target.value)}
              placeholder="How will you measure success?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Achievable
            </label>
            <input
              type="text"
              value={formData.smartGoals.achievable}
              onChange={(e) => updateNestedField('smartGoals', 'achievable', e.target.value)}
              placeholder="Is this goal realistic?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Relevant
            </label>
            <input
              type="text"
              value={formData.smartGoals.relevant}
              onChange={(e) => updateNestedField('smartGoals', 'relevant', e.target.value)}
              placeholder="Why is this goal important?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time-bound
            </label>
            <input
              type="text"
              value={formData.smartGoals.timeBound}
              onChange={(e) => updateNestedField('smartGoals', 'timeBound', e.target.value)}
              placeholder="What is the timeframe?"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="border-t pt-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Expected Outcomes</h3>
          <Button
            onClick={addOutcome}
            variant="outline"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Outcome
          </Button>
        </div>

        <div className="space-y-3">
          {formData.expectedOutcomes.map((outcome, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="text"
                value={outcome}
                onChange={(e) => updateOutcome(index, e.target.value)}
                placeholder={`Expected outcome #${index + 1}`}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              {formData.expectedOutcomes.length > 1 && (
                <button
                  onClick={() => removeOutcome(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Step 4: Budget Breakdown
export const Step4 = ({ formData, updateField }) => {
  const updateBudgetItem = (index, field, value) => {
    const breakdown = [...formData.budgetBreakdown];
    breakdown[index][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    
    // Recalculate percentages
    const total = breakdown.reduce((sum, item) => sum + item.amount, 0);
    breakdown.forEach(item => {
      item.percentage = total > 0 ? (item.amount / total) * 100 : 0;
    });
    
    updateField('budgetBreakdown', breakdown);
    updateField('totalBudget', total);
  };

  const addBudgetItem = () => {
    const breakdown = [...formData.budgetBreakdown, { category: '', amount: 0, percentage: 0 }];
    updateField('budgetBreakdown', breakdown);
  };

  const removeBudgetItem = (index) => {
    const breakdown = formData.budgetBreakdown.filter((_, i) => i !== index);
    updateField('budgetBreakdown', breakdown);
    
    // Recalculate total
    const total = breakdown.reduce((sum, item) => sum + item.amount, 0);
    updateField('totalBudget', total);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Budget Breakdown</h2>
      
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-teal-900">Total Budget</p>
            <p className="text-3xl font-bold text-teal-600 mt-1">
              ₹{formData.totalBudget.toLocaleString('en-IN')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-teal-700">
              ≈ ₹{(formData.totalBudget / 100000).toFixed(2)} Lakhs
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {formData.budgetBreakdown.map((item, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  value={item.category}
                  onChange={(e) => updateBudgetItem(index, 'category', e.target.value)}
                  placeholder="e.g., Personnel, Materials"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="col-span-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹)
                </label>
                <input
                  type="number"
                  value={item.amount}
                  onChange={(e) => updateBudgetItem(index, 'amount', e.target.value)}
                  placeholder="0"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  %
                </label>
                <div className="text-center py-2 font-semibold text-gray-700">
                  {item.percentage.toFixed(1)}%
                </div>
              </div>
              <div className="col-span-1 flex items-end justify-center pb-2">
                {formData.budgetBreakdown.length > 1 && (
                  <button
                    onClick={() => removeBudgetItem(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        onClick={addBudgetItem}
        variant="outline"
        className="w-full"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Budget Category
      </Button>

      <div className="bg-gray-50 rounded-lg p-4 mt-6">
        <h4 className="font-semibold text-gray-900 mb-3">Budget Summary</h4>
        <div className="space-y-2">
          {formData.budgetBreakdown.map((item, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <span className="text-gray-700">{item.category || `Category ${index + 1}`}</span>
              <span className="font-semibold text-gray-900">
                ₹{item.amount.toLocaleString('en-IN')} ({item.percentage.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Step 5: Timeline
export const Step5 = ({ formData, updateField }) => {
  const calculateDuration = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const months = Math.ceil((end - start) / (1000 * 60 * 60 * 24 * 30));
      return months;
    }
    return 0;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Timeline</h2>
      
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => updateField('startDate', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => updateField('endDate', e.target.value)}
            min={formData.startDate}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
      </div>

      {formData.startDate && formData.endDate && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-900 mb-1">Project Duration</p>
          <p className="text-2xl font-bold text-blue-600">
            {calculateDuration()} months
          </p>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-6 mt-8">
        <h3 className="font-bold text-gray-900 mb-4">Project Summary</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Title:</span>
            <span className="font-semibold text-gray-900 text-right max-w-md">{formData.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Category:</span>
            <span className="font-semibold text-gray-900">{formData.scheduleVIICategory}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Location:</span>
            <span className="font-semibold text-gray-900">
              {formData.location.district}, {formData.location.state}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Beneficiaries:</span>
            <span className="font-semibold text-gray-900">
              {formData.targetBeneficiaries.count} {formData.targetBeneficiaries.category}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Budget:</span>
            <span className="font-semibold text-teal-600">
              ₹{formData.totalBudget.toLocaleString('en-IN')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Duration:</span>
            <span className="font-semibold text-gray-900">{calculateDuration()} months</span>
          </div>
        </div>
      </div>
    </div>
  );
};
